/**
 * The one place in the app that touches `window.history`.
 *
 * Everything navigational goes through `navigate`, which asks `classify` whether the transition is
 * a place (push) or a refinement (replace). **That default is the whole point**: a surface added
 * next year gets correct Back behaviour without its author having to know this file exists, whereas
 * a convention like "remember to push here" is one nobody will remember. The two acts that are
 * genuinely exceptions — closing a reading tab, and a deliberate amend — pass `{ replace: true }`.
 *
 * ## Why the writes are ordered the way they are
 *
 * - **A push flushes a pending amend first.** The debounce means the entry you are leaving may not
 *   yet have its final URL; pushing without flushing would leave "the search I had typed" out of
 *   the entry Back returns to.
 * - **A pop DROPS a pending amend instead of flushing it.** By the time `popstate` fires the browser
 *   has already moved, so writing would land the old state on the entry you just arrived at. This
 *   is the sharpest edge in the whole design: it is a ~200ms window, and it silently corrupts the
 *   entry rather than failing.
 * - **Every write reads `window.location.search` live**, never a captured value, or it resurrects
 *   the capture link's `q` — see `navUrl.ts`.
 *
 * Pushes are gesture-bound (at most one per click or key press that means "go somewhere"), so they
 * need no debounce of their own and stay far inside Safari's ~100-writes-per-30-seconds limit,
 * which counts pushes and replaces together and then silently drops the rest.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  applyPatch,
  classify,
  popOverlay,
  pushOverlay,
  readEntryState,
  resolvePop,
  type EntryState,
  type NavPatch,
  type OverlayId,
} from "./navHistory";
import { loadStoredSurfaces, restoreNavState, saveStoredSurfaces } from "./navSession";
import { serializeNavUrl, withoutCaptureQuery, type NavState } from "./navUrl";

const AMEND_DELAY_MS = 200;

export interface NavApi {
  /**
   * Go somewhere, or refine where you are. `classify` decides which; `replace` forces the latter
   * for the cases state alone cannot distinguish — closing a reading tab changes the active log
   * exactly as clicking one does, but the place you were is gone rather than behind you.
   */
  navigate(
    patch: NavPatch | ((prev: NavState) => NavPatch),
    opts?: { replace?: boolean },
  ): void;
  /** Give a layer its own history entry, so Back closes it. The URL does not change. */
  openOverlay(id: OverlayId): void;
  /**
   * Consume the entry `openOverlay` created, after closing the layer yourself.
   *
   * The caller closes its own UI first and synchronously — waiting for the `popstate` this triggers
   * would leave the lightbox on screen for a frame after you pressed Escape.
   */
  closeOverlay(id: OverlayId): void;
  /**
   * Stop tracking a layer **without touching history** — for a close that is itself a navigation.
   *
   * `closeOverlay` consumes the layer's entry with `history.back()`, which is asynchronous, so
   * `closeOverlay(id)` followed by `navigate(...)` is a race the navigation loses: the push lands
   * first and the pop then undoes it. That is not hypothetical — it is what picking a row in the
   * phone's その他 sheet did (日本-497), and it presented as a tap that did nothing at all.
   *
   * A row that navigates wants the opposite bookkeeping: the layer is gone, its entry is about to
   * be **superseded by the push** rather than consumed, and nothing should go back. Forget it
   * first, then navigate — which is also what makes the new entry record an honest `overlays`,
   * since `entryFor` reads this list at push time.
   *
   * Deliberately not a flag on `closeOverlay`: the two do different things to history, and a
   * boolean argument would let a call site pick the wrong one by omission.
   */
  forgetOverlay(id: OverlayId): void;
  /**
   * Register how to close a layer **without touching history**, for when Back is what closed it.
   * Returns the unregister function.
   */
  registerOverlay(id: OverlayId, closeQuietly: () => void): () => void;
}

export interface HistoryNav {
  nav: NavState;
  api: NavApi;
  /**
   * Bumped on every pop that commits state.
   *
   * Surfaces that restore something once and then forget it — Cards' open dock is the only one
   * today — re-arm on this. Without it, Back to a different card changes `cards.note` but nothing
   * re-reads it, because the consumed-once ref was spent at mount.
   */
  epoch: number;
  /**
   * `window.location.search` as it was on the very first render, capture params and all.
   *
   * The capture deep link is read from here rather than from the live location, which is what lets
   * boot strip `q` immediately instead of depending on this hook's effect running after the
   * consumer's. That ordering used to be load-bearing and undocumented.
   */
  initialSearch: string;
}

export function useHistoryNav(): HistoryNav {
  const initialSearch = useRef(typeof window === "undefined" ? "" : window.location.search);

  // The URL for the surface it names, this tab's session store for the ones it does not — see
  // `navSession.ts`. The URL wins per surface wherever it speaks, so following someone's link never
  // shows your own filters instead of theirs.
  const [nav, setNav] = useState<NavState>(() =>
    restoreNavState(initialSearch.current, loadStoredSurfaces()),
  );
  const [epoch, setEpoch] = useState(0);

  /** The live values the listener and the callbacks read; both are registered once. */
  const navRef = useRef(nav);
  const idxRef = useRef(0);
  const overlaysRef = useRef<readonly OverlayId[]>([]);
  /** Set while we are consuming an entry on the app's own behalf — see `closeOverlay`. */
  const consumingRef = useRef<OverlayId | null>(null);
  const closersRef = useRef(new Map<OverlayId, () => void>());
  /**
   * Which entry each open layer's own `pushState` created.
   *
   * `closeOverlay` consumes that entry with `history.back()`, which is only correct while it is
   * still the entry Back would reach. A push made *while* a layer was open buries it — clicking a
   * reading tab behind the radical picker is enough — and going back then undoes the push instead
   * of shutting something already shut.
   */
  const overlayIdxRef = useRef(new Map<OverlayId, number>());
  const amendTimer = useRef<number | null>(null);
  const amendPending = useRef<NavState | null>(null);

  const entryFor = useCallback(
    (state: NavState, overlays: readonly OverlayId[] = overlaysRef.current): EntryState => ({
      idx: idxRef.current,
      nav: state,
      overlays,
    }),
    [],
  );

  const writeReplace = useCallback(
    (state: NavState) => {
      const search = serializeNavUrl(window.location.search, state);
      window.history.replaceState(entryFor(state), "", window.location.pathname + search);
    },
    [entryFor],
  );

  const cancelAmend = useCallback(() => {
    if (amendTimer.current !== null) window.clearTimeout(amendTimer.current);
    amendTimer.current = null;
    amendPending.current = null;
  }, []);

  const flushAmend = useCallback(() => {
    const pending = amendPending.current;
    if (amendTimer.current !== null) window.clearTimeout(amendTimer.current);
    amendTimer.current = null;
    amendPending.current = null;
    if (pending) writeReplace(pending);
  }, [writeReplace]);

  const navigate = useCallback<NavApi["navigate"]>(
    (patch, opts) => {
      const prev = navRef.current;
      const resolved = typeof patch === "function" ? patch(prev) : patch;
      const next = applyPatch(prev, resolved);

      const classified = classify(prev, next);
      if (classified === "none") return;
      const effect = opts?.replace ? "replace" : classified;

      navRef.current = next;
      setNav(next);

      if (effect === "push") {
        flushAmend();
        idxRef.current += 1;
        const search = serializeNavUrl(window.location.search, next);
        window.history.pushState(entryFor(next), "", window.location.pathname + search);
        return;
      }

      // Debounced: the free-text field re-renders per keystroke, and Safari drops history writes
      // past its rate limit rather than reporting them.
      amendPending.current = next;
      if (amendTimer.current !== null) window.clearTimeout(amendTimer.current);
      amendTimer.current = window.setTimeout(() => {
        amendTimer.current = null;
        const state = amendPending.current;
        amendPending.current = null;
        if (state) writeReplace(state);
      }, AMEND_DELAY_MS);
    },
    [entryFor, flushAmend, writeReplace],
  );

  const openOverlay = useCallback<NavApi["openOverlay"]>(
    (id) => {
      if (overlaysRef.current.includes(id)) return;
      flushAmend();
      const overlays = pushOverlay(overlaysRef.current, id);
      overlaysRef.current = overlays;
      idxRef.current += 1;
      overlayIdxRef.current.set(id, idxRef.current);
      // The same URL: a layer is not a location, and a refresh must not restore one.
      window.history.pushState(
        entryFor(navRef.current, overlays),
        "",
        window.location.pathname + window.location.search,
      );
    },
    [entryFor, flushAmend],
  );

  const closeOverlay = useCallback<NavApi["closeOverlay"]>((id) => {
    // A layer that never got an entry — closed twice, or closed by the pop that was already
    // consuming it — must not eat somebody else's.
    if (!overlaysRef.current.includes(id)) return;
    // Nor may one whose entry has since been buried. `history.back()` reaches whatever is on top,
    // so once a push has landed above the layer's own entry it would undo THAT — taking you
    // somewhere rather than shutting something you have already shut. Drop the bookkeeping and
    // leave the now-inert entry alone; a Back that reaches it finds nothing to close.
    if (overlayIdxRef.current.get(id) !== idxRef.current) {
      overlaysRef.current = popOverlay(overlaysRef.current, id);
      overlayIdxRef.current.delete(id);
      return;
    }
    consumingRef.current = id;
    window.history.back();
  }, []);

  const forgetOverlay = useCallback<NavApi["forgetOverlay"]>((id) => {
    overlaysRef.current = popOverlay(overlaysRef.current, id);
    overlayIdxRef.current.delete(id);
  }, []);

  const registerOverlay = useCallback<NavApi["registerOverlay"]>((id, closeQuietly) => {
    closersRef.current.set(id, closeQuietly);
    return () => {
      if (closersRef.current.get(id) !== closeQuietly) return;
      closersRef.current.delete(id);
      // The owner has unmounted — a rail click away from Read takes the radical picker with it —
      // so whatever it had open is shut whether or not anything said so. Stop claiming otherwise:
      // left in, the next `openOverlay` early-returns and pushes NO entry, and the next
      // `closeOverlay` then consumes a real one.
      overlaysRef.current = popOverlay(overlaysRef.current, id);
      overlayIdxRef.current.delete(id);
    };
  }, []);

  /**
   * Boot: agree the URL, the state and entry 0 with each other, in one write.
   *
   * Ref-guarded because StrictMode double-invokes mount effects in development. This also strips
   * the capture params — the only place that happens, and it happens before any entry exists, which
   * is what makes it impossible for Back to land on a URL that still carries a capture.
   */
  const booted = useRef(false);
  useEffect(() => {
    if (booted.current) return;
    booted.current = true;
    const search = serializeNavUrl(withoutCaptureQuery(window.location.search), navRef.current);
    window.history.replaceState(
      entryFor(navRef.current),
      "",
      window.location.pathname + search,
    );
  }, [entryFor]);

  useEffect(() => {
    const onPop = (event: PopStateEvent) => {
      // Drop, never flush: the browser has already moved, so a write now lands on the entry we
      // just arrived at rather than the one we left.
      cancelAmend();

      const consuming = consumingRef.current;
      if (consuming) {
        // Our own `history.back()`, from a layer the user closed some other way. The UI is already
        // updated and the URL never changed; only the bookkeeping needs to catch up.
        consumingRef.current = null;
        overlaysRef.current = popOverlay(overlaysRef.current, consuming);
        overlayIdxRef.current.delete(consuming);
        idxRef.current = readEntryState(event.state)?.idx ?? Math.max(idxRef.current - 1, 0);
        return;
      }

      const result = resolvePop(
        { overlays: overlaysRef.current },
        event.state,
        window.location.search,
      );
      for (const id of result.overlaysToClose) {
        closersRef.current.get(id)?.();
        overlayIdxRef.current.delete(id);
      }
      overlaysRef.current = result.overlays;
      idxRef.current = result.idx;
      navRef.current = result.nav;
      setNav(result.nav);
      setEpoch((value) => value + 1);
    };

    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [cancelAmend]);

  /**
   * Keep this tab's memory of the off-screen surfaces current.
   *
   * An effect rather than a call beside each write, because a pop commits a whole snapshot from
   * `history.state` and would otherwise leave the store describing where you were before it.
   */
  useEffect(() => {
    saveStoredSurfaces(nav);
  }, [nav]);

  const api = useMemo<NavApi>(
    () => ({ navigate, openOverlay, closeOverlay, forgetOverlay, registerOverlay }),
    [navigate, openOverlay, closeOverlay, forgetOverlay, registerOverlay],
  );

  return { nav, api, epoch, initialSearch: initialSearch.current };
}
